import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Box, Button, LinearProgress, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import {
  getMarginCenterCustomerRelationships,
  getMarginCenterProductCustomers,
} from '../services/marginCenterService';
import {
  formatMarginCenterCurrency,
  formatMarginCenterNumber,
} from '../utils/numberFormat';
import type {
  MarginCenterCustomerProductPositioning,
  MarginCenterCustomerProductSelection,
} from '../types/marginCenter';

type Side = 'customer' | 'product';

type PortfolioRow = {
  id: string;
  label: string;
  active: boolean;
  revenue: number;
  share: number | null;
  change: number | null;
};

type PortfolioPage = {
  status: string;
  rows: PortfolioRow[];
  hasMore: boolean;
  nextCursor: string | null;
  negativeItems: number | null;
  portfolioTotal: number | null;
};

const CHART_COLORS = [
  '#818cf8', '#2dd4bf', '#fbbf24', '#fb7185', '#a78bfa',
];

function pct(value: number | null): string {
  return value === null ? '—' : `${formatMarginCenterNumber(value)} %`;
}
function signedPoints(value: number | null): string {
  return value === null ? '—' : `${value > 0 ? '+' : ''}${formatMarginCenterNumber(value)} p.p.`;
}

export default function CustomerRelationshipPositionComposition({
  side, customerId, productId, customerLabel, productLabel,
  positioning, selection, currencyCode,
}: {
  side: Side;
  customerId: string;
  productId: string;
  customerLabel: string;
  productLabel: string;
  positioning: MarginCenterCustomerProductPositioning;
  selection: MarginCenterCustomerProductSelection;
  currencyCode: string | null;
}) {
  const [cursorStack, setCursorStack] = useState<string[]>(['']);
  const cursor = cursorStack[cursorStack.length - 1] || undefined;
  const params = useMemo(() => ({
    start_date: selection.start_date ?? undefined,
    end_date: selection.end_date ?? undefined,
    comparison_mode: selection.comparison_mode,
    comparison_start_date: selection.comparison_mode === 'custom'
      ? selection.comparison_start_date ?? undefined : undefined,
    comparison_end_date: selection.comparison_mode === 'custom'
      ? selection.comparison_end_date ?? undefined : undefined,
    sort_field: 'revenue' as const,
    sort_direction: 'desc' as const,
    limit: 50,
    cursor,
  }), [selection, cursor]);

  const query = useQuery({
    queryKey: [
      'relationship-position-composition', side, customerId, productId, params,
    ],
    enabled: Boolean(customerId) && Boolean(productId),
    queryFn: async (): Promise<PortfolioPage> => {
      if (side === 'customer') {
        const result = await getMarginCenterCustomerRelationships(customerId, params);
        return {
          status: result.status,
          rows: result.rows.map((row) => ({
            id: row.relationship.product_id,
            label: row.relationship.label,
            active: row.activity.has_current_activity,
            revenue: row.current.revenue,
            share: row.mix.current_revenue_share_pct,
            change: row.mix.revenue_share_delta_points,
          })),
          hasMore: result.page.has_more,
          nextCursor: result.page.next_cursor,
          negativeItems: result.portfolio_analysis?.concentration.revenue.current.negative_revenue_products ?? null,
          portfolioTotal: result.portfolio_analysis?.concentration.revenue.current.total_revenue ?? null,
        };
      }
      const result = await getMarginCenterProductCustomers(productId, params);
      return {
        status: result.status,
        rows: result.rows.map((row) => ({
          id: row.relationship.customer_id,
          label: row.customer.label,
          active: row.activity.current_active,
          revenue: row.current.revenue,
          share: row.current.revenue_share_pct,
          change: row.changes?.revenue_share_delta_points ?? null,
        })),
        hasMore: result.page.has_more,
        nextCursor: result.page.next_cursor,
        negativeItems: result.portfolio_analysis?.concentration.revenue.current.negative_revenue_customers ?? null,
        portfolioTotal: result.portfolio_analysis?.concentration.revenue.current.total_revenue ?? null,
      };
    },
  });

  const item = side === 'customer'
    ? {
        id: productId,
        label: productLabel,
        total: positioning.current.customer_total_revenue,
        share: positioning.current.relationship_share_of_customer_revenue_pct,
        rank: positioning.current.product_rank_within_customer,
        active: positioning.current.customer_active_products,
        kind: 'producto',
        plural: 'productos',
      }
    : {
        id: customerId,
        label: customerLabel,
        total: positioning.current.product_total_revenue,
        share: positioning.current.relationship_share_of_product_revenue_pct,
        rank: positioning.current.customer_rank_within_product,
        active: positioning.current.product_active_customers,
        kind: 'cliente',
        plural: 'clientes',
      };

  const rows = query.data?.rows ?? [];
  const topFive = cursorStack.length === 1
    ? rows.filter((row) => row.active && row.revenue > 0).slice(0, 5)
    : [];
  const topTotal = topFive.reduce((acc, row) => acc + row.revenue, 0);
  const rest = item.total - topTotal;
  const consistentTotal = query.data?.portfolioTotal !== null
    && query.data?.portfolioTotal !== undefined
    && Math.abs(query.data.portfolioTotal - item.total) <= Math.max(0.02, Math.abs(item.total) * 0.000001);
  const canShowChart = cursorStack.length === 1
    && item.active > 1
    && item.total > 0
    && query.data?.negativeItems === 0
    && consistentTotal
    && topFive.length > 0
    && rest >= -0.02;
  const chartData = canShowChart ? [
    ...topFive.map((row, index) => ({
      id: row.id,
      label: row.label,
      value: row.revenue,
      color: row.id === item.id ? '#38bdf8' : CHART_COLORS[index],
    })),
    ...(rest > 0.02 ? [{
      id: 'rest', label: 'Resto (neto)', value: rest, color: '#64748b',
    }] : []),
  ] : [];

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 800, mt: 0.8 }}>
        {side === 'customer' ? 'Ventas del cliente por producto' : 'Ventas del producto por cliente'}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: 0.2 }}>
        {item.label} · {pct(item.share)} · {item.rank === null ? 'Sin posición calculable' : `puesto ${formatMarginCenterNumber(item.rank, 0)} de ${formatMarginCenterNumber(item.active, 0)}`}
      </Typography>
      {(!customerId || !productId) && (
        <Alert severity="info" sx={{ mt: 1 }}>La relación necesita un cliente y un producto identificados.</Alert>
      )}
      {query.isPending && Boolean(customerId) && Boolean(productId) && <LinearProgress sx={{ mt: 1.2 }} />}
      {query.isError && <Alert severity="error" sx={{ mt: 1 }}>No se pudo cargar la composición. Puedes volver a intentarlo al abrir la tarjeta.</Alert>}
      {query.data && query.data.status !== 'ok' && (
        <Typography variant="body2" sx={{ mt: 1, color: '#94a3b8' }}>
          No hay una cartera consultable para este periodo.
        </Typography>
      )}
      {query.data?.status === 'ok' && (
        <>
          {item.active === 1 && (
            <Alert severity="info" sx={{ mt: 1.1 }}>
              {side === 'customer'
                ? 'Este es el único producto activo del cliente en el periodo.'
                : 'Este es el único cliente comprador activo del producto en el periodo.'}
            </Alert>
          )}
          {cursorStack.length === 1 && item.active > 1 && canShowChart && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '240px minmax(0, 1fr)' }, gap: 1.5, alignItems: 'center', mt: 1 }}>
              <Box sx={{ height: 205, minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={chartData} dataKey="value" nameKey="label" innerRadius={51} outerRadius={78} stroke="none" isAnimationActive={false}>
                      {chartData.map((entry) => <Cell key={entry.id} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(value) => formatMarginCenterCurrency(Number(value), currencyCode)} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 850 }}>Cinco mayores y resto</Typography>
                {chartData.map((entry) => (
                  <Stack key={entry.id} direction="row" alignItems="center" spacing={0.8} sx={{ mt: 0.55 }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: 0.5, bgcolor: entry.color, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: entry.id === item.id ? '#7dd3fc' : '#cbd5e1' }}>
                      {entry.label}{entry.id === item.id ? ' · seleccionado' : ''}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#f8fafc', fontWeight: 800, flexShrink: 0 }}>
                      {pct(100 * entry.value / item.total)}
                    </Typography>
                  </Stack>
                ))}
                {!topFive.some((row) => row.id === item.id) && (
                  <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: 0.7 }}>
                    La relación seleccionada está incluida en «Resto»; su peso figura arriba.
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          {cursorStack.length === 1 && item.active > 1 && !canShowChart && (
            <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: 1 }}>
              Gráfico de sectores no disponible: el total no es positivo, hay ingresos negativos o no se ha podido verificar la composición completa. La tabla conserva los importes observados.
            </Typography>
          )}
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1} sx={{ mt: 1.25, mb: 0.55 }}>
            <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 850 }}>
              {side === 'customer' ? 'Productos del cliente' : 'Clientes del producto'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              Página {cursorStack.length} · orden por ingresos
            </Typography>
          </Stack>
          <TableContainer sx={{ maxHeight: 265, border: '1px solid rgba(148,163,184,0.16)', borderRadius: 1.5, overflowY: 'auto' }}>
            <Table size="small" stickyHeader sx={{ '& .MuiTableCell-head': { bgcolor: '#142135', color: '#94a3b8' }, '& .MuiTableCell-root': { borderBottom: '1px solid rgba(148,163,184,0.12)', fontSize: '0.73rem' } }}>
              <TableHead><TableRow>
                <TableCell>{item.kind === 'producto' ? 'Producto' : 'Cliente'}</TableCell>
                <TableCell align="right">Ingresos</TableCell>
                <TableCell align="right">Peso</TableCell>
                <TableCell align="right">Cambio</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} sx={{ bgcolor: row.id === item.id ? 'rgba(56,189,248,0.11)' : 'transparent' }}>
                    <TableCell sx={{ color: row.id === item.id ? '#7dd3fc' : '#e2e8f0', fontWeight: row.id === item.id ? 850 : 500 }}>
                      {row.label}{row.id === item.id ? ' · seleccionado' : ''}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#e2e8f0', whiteSpace: 'nowrap' }}>
                      {row.active ? formatMarginCenterCurrency(row.revenue, currencyCode) : 'Sin actividad'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#e2e8f0', whiteSpace: 'nowrap' }}>
                      {row.active ? pct(row.share) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: row.change === null || row.change === 0 ? '#94a3b8' : row.change > 0 ? '#34d399' : '#fb7185', whiteSpace: 'nowrap' }}>
                      {signedPoints(row.change)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {rows.some((row) => !row.active) && (
            <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: 0.5 }}>También figuran relaciones presentes solo en la comparación, marcadas como sin actividad actual.</Typography>
          )}
          {rows.length === 0 && (
            <Typography variant="body2" sx={{ color: '#94a3b8', mt: 1 }}>No hay filas disponibles en esta página.</Typography>
          )}
          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 0.75 }}>
            <Button variant="outlined" size="small" disabled={cursorStack.length <= 1 || query.isFetching} onClick={() => setCursorStack((prev) => prev.slice(0, -1))} sx={{ textTransform: 'none' }}>
              Anterior
            </Button>
            <Button variant="outlined" size="small" disabled={!query.data?.hasMore || !query.data?.nextCursor || query.isFetching} onClick={() => {
              const next = query.data?.nextCursor;
              if (next) setCursorStack((prev) => [...prev, next]);
            }} sx={{ textTransform: 'none' }}>
              Siguiente
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
}
